/**
 * useNominationList — one fetch of the nominations this caller may see.
 *
 * `ListNominations` takes no parameters: the service scopes the result to the caller
 * (marketers and admins get every nomination; anyone else gets their own plus those created
 * by their direct and indirect reports). So every surface needs the same whole set and
 * narrows it in memory — the nomination list page, the event detail page's attendance list,
 * and the review counts on both landing pages.
 *
 * The same shape as useEventList, deliberately: aborts on unmount, ignores a late response,
 * keeps "empty" and "failed" apart, and can be disabled so a page that will refuse the
 * caller does not fire a request a render-time early return cannot stop.
 */

import { useCallback, useEffect, useState } from 'react';
import { eventHubCalendarClient, type Nomination } from '../client/EventCalendarClient';

export interface NominationListState {
  nominations: readonly Nomination[];
  loading: boolean;
  /** Error message, or null. Empty-vs-failed are different states for the UI. */
  error: string | null;
  /** Re-fetches — after a decision, or to retry. */
  reload: () => void;
  /**
   * Swaps one nomination for the version the service just returned (matched by id), without a
   * re-fetch. For a single-row change such as an attendance confirmation, where reloading the
   * whole list would flash the loading overlay for one checkbox.
   */
  replace: (updated: Nomination) => void;
}

export function useNominationList(enabled = true): NominationListState {
  const [nominations, setNominations] = useState<readonly Nomination[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const replace = useCallback((updated: Nomination) => {
    setNominations((list) =>
      list.map((n) => (n.nominationId === updated.nominationId ? updated : n)),
    );
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(null);

    eventHubCalendarClient()
      .listNominations({ signal: controller.signal })
      .then((result) => {
        if (cancelled) return;
        setNominations(result);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setNominations([]);
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [nonce, enabled]);

  return { nominations, loading, error, reload, replace };
}
